import { lazy, Suspense } from 'react';

import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth-context';

const ReportingOverview = lazy(() =>
  import('./ReportingOverview').then((m) => ({ default: m.ReportingOverview })),
);

/**
 * Tableau de bord (Sprint 6b) : remplace le placeholder du Sprint 0. Reste
 * volontairement léger — la section KPI/graphiques (`ReportingOverview`,
 * dépendance `recharts`) est chargée à la demande (`React.lazy`, voir Sprint
 * 7 — réduction du bundle) puisqu'un agent de terrain sans `reporting.view`
 * ne la voit jamais.
 *
 * **Choix de garde d'accès** (documenté comme demandé par le sprint) : la
 * page elle-même reste accessible à tout utilisateur connecté (pas de
 * `<RequireAuth permission="reporting.view">` sur la route `/`, qui doit
 * rester le point d'entrée universel de l'espace authentifié). En revanche,
 * la section KPI/graphiques — qui agrège des données transverses à toute la
 * plateforme, pas seulement le périmètre d'un agent de terrain — n'est rendue
 * que si `hasPermission('reporting.view')`, exactement comme un lien de
 * navigation masqué dans `AppShell.tsx`. Un agent sans ce droit (agent
 * documentaire, biométrique, carte, logistique, remise ou contrôle — voir
 * `packages/shared/src/constants/role-permissions.ts`) voit un message de
 * bienvenue simple à la place, jamais une erreur 403 déclenchée par une
 * requête qu'il n'a pas le droit de faire.
 */
export function DashboardPage() {
  const { user, hasPermission } = useAuth();
  const canViewReporting = hasPermission('reporting.view');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Tableau de bord</h1>
        <p className="text-muted-foreground">Bienvenue, {user?.nomComplet}.</p>
      </div>

      {canViewReporting ? (
        <Suspense fallback={<Skeleton className="h-24 w-full" />}>
          <ReportingOverview />
        </Suspense>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Connexion et sécurité opérationnelles</CardTitle>
            <CardDescription>
              Retrouvez vos outils de travail dans le menu de navigation à gauche.
            </CardDescription>
          </CardHeader>
        </Card>
      )}
    </div>
  );
}
